import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HttpService } from '../../game/services/HttpService';
import './Login.component.css';
import logo from '../../assets/compressed/logoMainH-min.png';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccessMessage, setResetSuccessMessage] = useState<string | null>(null);

  const httpService = new HttpService();

  React.useEffect(() => {
    const state = location.state as { resetSuccess?: boolean } | null;
    if (state?.resetSuccess) {
      setResetSuccessMessage('Password reset successfully. You can now log in.');
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  const validateEmail = (email: string): boolean => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const emailNormalized = email.trim().toLowerCase();

    if (!validateEmail(emailNormalized)) {
      setError('Email is not valid');
      setLoading(false);
      return;
    }

    if (password.length < 5) {
      setError('Password must be at least 5 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await httpService.post<any>('user/signin', {
        email: emailNormalized,
        password,
        gameVersionToken: '1.0_sdhkfHjcbdk348HDJWKHEK7238DGKWEKJH7239fbBDEWKD723iudbdkfwkhfbndGGDBCEkjdckjJJJ23BDebwekfwhKJffsdfbJjewefb247d4h4bf34fbbHHAAXb3h8fkfjf',
      });

      console.log('Login response:', response);

      // Handle different response structures
      const responseData = response.data || response;
      
      console.log('Response data:', responseData);
      
      if (responseData && responseData.token) {
        const user = responseData.user || responseData;
        const userName = user.firstName && user.lastName 
          ? `${user.firstName} ${user.lastName}`
          : user.name || user.username || 'Player';
        
        console.log('User data:', user);
        console.log('User name:', userName);
        
        // Store auth info
        localStorage.setItem('token', responseData.token);
        localStorage.setItem('userId', user.id || user.userId);
        localStorage.setItem('username', userName);
        if (user.email) {
          localStorage.setItem('userEmail', user.email);
        }
        // Store email verification status
        if (user.emailVerified !== undefined) {
          localStorage.setItem('emailVerified', String(user.emailVerified));
        } else {
          // Default to false if not provided
          localStorage.setItem('emailVerified', 'false');
        }

        console.log('Auth info stored in localStorage');

        // Get roomId from URL if present
        const roomId = searchParams.get('roomId');
        
        // Navigate to dashboard or game
        if (localStorage.getItem('emailVerified') !== 'true') {
          navigate('/verify-email');
          return;
        } else {
          if (roomId) {
            navigate(`/game?roomId=${roomId}`);
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        console.error('No token in response:', response);
        setError(response.message || response.error || 'Login failed - no token received');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const msg = err.message || '';
      const isInvalidCredentials =
        msg.toLowerCase().includes('invalid password') ||
        msg.toLowerCase().includes('user not found') ||
        msg.toLowerCase().includes('invalid email');
      setError(isInvalidCredentials ? 'Invalid email or password. Please try again.' : msg || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="game-auth-container">
      <div className="game-auth-background" />
      
      <div className="game-auth-content">
        <motion.div
          className="game-auth-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="game-auth-header">
            <img className="header-logo" src={logo} alt="Wild Chips Poker" onClick={() => navigate('/')} />
            <p>Login to your account</p>
          </div>

          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.div>
          )}
          {resetSuccessMessage && (
            <motion.div
              className="success-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {resetSuccessMessage}
            </motion.div>
          )}

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group password-with-toggle">
              <label>Password</label>
              <div className="password-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={5}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.932 12c1.292 4.338 5.31 7.5 10.068 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.068 7.5a10.523 10.523 0 01-1.724 2.547m-3.75-3.75a10.5 10.5 0 01-4.5-4.5m4.5 4.5a10.5 10.5 0 01-4.5-4.5" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Login'}
            </button>

            <div className="forgot-password-wrap">
              <button
                type="button"
                className="link-button"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </button>
            </div>
          </form>

          <div className="auth-switch">
            <p>
              Don't have an account?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => navigate('/signup')}
              >
                Sign Up
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;

